<template>
  <div class="header-root">
    <div
      class="header-bar fixed inset-x-0 top-0 z-[100]"
      :class="{ 'header-bar--hidden': isHeaderHidden }"
    >
    <nav
      :class="[
        'backdrop-blur-md header-nav overflow-hidden',
        navScrolled ? 'nav-scrolled' : 'nav-top',
      ]"
    >
      <div
        class="container mx-auto flex items-center justify-between gap-3 header-container"
      >
        <router-link
          to="/"
          class="group logo-link min-w-0 flex-1 lg:flex-none transition-all duration-300"
        >
          <div class="logo-frame text-left">
            <div
              class="logo-title logo-title--hero font-bold transition-all duration-300 group-hover:-translate-y-px group-hover:scale-[1.01] transform-gpu tracking-wide leading-none relative z-10"
            >
              Amadeen Consult
            </div>
            <div
              class="tagline tagline--hero font-normal tracking-wide relative z-10"
            >
              Stay. Live. Own.
            </div>
          </div>
        </router-link>

        <div class="hidden lg:flex items-center gap-0.5 shrink-0">
          <div class="flex items-center gap-0.5">
            <router-link
              to="/"
              class="nav-link px-3 py-1.5 text-[#d4af37] hover:text-white transition-all duration-300 rounded-md font-medium tracking-wide text-sm"
              >Home</router-link
            >

            <router-link
              to="/bio"
              class="nav-link px-3 py-1.5 text-[#d4af37] hover:text-white transition-all duration-300 rounded-md font-medium tracking-wide text-sm"
              >Bio</router-link
            >

            <router-link
              to="/list-with-us"
              class="nav-link px-3 py-1.5 text-[#d4af37] hover:text-white transition-all duration-300 rounded-md font-medium tracking-wide text-sm"
              >List with Us</router-link
            >
          </div>

          <div class="flex items-center gap-2 ml-2 pl-2 border-l border-[#d4af37]/20">
            <button
              @click="openBookingModal"
              class="book-button px-3.5 py-1.5 bg-gradient-to-r from-[var(--zulu-bronze)] to-[var(--zulu-gold)] hover:from-[var(--zulu-gold)] hover:to-[var(--zulu-bronze)] text-white font-semibold rounded-md transition-all duration-300 hover:scale-[1.02] hover:shadow-lg text-sm whitespace-nowrap"
            >
              Book with Us
            </button>

            <button
              id="theme-toggle"
              type="button"
              class="p-1.5 text-[#d4af37] flex items-center justify-center border border-[#d4af37]/40 rounded-md hover:bg-[#d4af37]/20 transition-all duration-300 backdrop-blur-sm hover:border-[#d4af37]/60"
              aria-label="Toggle theme"
            >
              <svg
                id="theme-toggle-dark-icon"
                class="hidden w-4 h-4"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
                ></path>
              </svg>
              <svg
                id="theme-toggle-light-icon"
                class="hidden w-4 h-4"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </button>
          </div>
        </div>

        <button
          class="lg:hidden menu-toggle shrink-0"
          id="navbar-toggle"
          @click="toggleMobileMenu"
          aria-label="Toggle menu"
          :aria-expanded="!mobileMenuHidden"
        >
          <span class="toggler-icon top-bar"></span>
          <span class="toggler-icon middle-bar"></span>
          <span class="toggler-icon bottom-bar"></span>
        </button>
      </div>
    </nav>
    </div>

    <Teleport to="body">
    <div
      class="lg:hidden mobile-menu-overlay fixed inset-0 bg-black/60 z-[110] transition-opacity duration-200"
      :class="{
        'opacity-0 pointer-events-none': mobileMenuHidden,
        'opacity-100': !mobileMenuHidden,
      }"
      @click="closeMobileMenu"
    ></div>

    <div
      class="lg:hidden mobile-menu-panel fixed top-0 right-0 bottom-0 z-[120] w-[min(18rem,85vw)] bg-white dark:bg-[#141414] border-l border-[var(--zulu-bronze)]/25 dark:border-[var(--zulu-gold)]/25 shadow-2xl transition-transform duration-300 ease-out"
      :class="mobileMenuHidden ? 'translate-x-full' : 'translate-x-0'"
    >
      <div class="flex flex-col h-full min-h-0">
        <div class="drawer-header">
          <router-link
            to="/"
            class="drawer-brand min-w-0"
            @click="closeMobileMenu"
          >
            <span class="drawer-brand-title">Amadeen Consult</span>
            <span class="drawer-brand-tagline">Stay. Live. Own.</span>
          </router-link>
          <button
            @click="closeMobileMenu"
            class="drawer-close"
            aria-label="Close menu"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <nav class="drawer-nav overflow-y-auto flex-1 min-h-0" aria-label="Mobile">
          <router-link
            to="/"
            class="drawer-link"
            @click="closeMobileMenu"
          >
            Home
          </router-link>

          <router-link
            to="/bio"
            class="drawer-link"
            @click="closeMobileMenu"
          >
            Bio
          </router-link>

          <router-link
            to="/list-with-us"
            class="drawer-link"
            @click="closeMobileMenu"
          >
            List with Us
          </router-link>

          <button
            type="button"
            @click="openBookingModal"
            class="drawer-cta"
          >
            Book with Us
          </button>
        </nav>

        <div class="drawer-footer">
          <span class="drawer-footer-label">Theme</span>
          <button
            id="mobile-theme-toggle"
            type="button"
            class="drawer-theme-btn"
            aria-label="Toggle theme"
          >
              <svg
                id="mobile-theme-toggle-dark-icon"
                class="hidden w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
                ></path>
              </svg>
              <svg
                id="mobile-theme-toggle-light-icon"
                class="hidden w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                ></path>
              </svg>
          </button>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- Booking Modal -->
    <EventTicketModal
      v-if="showBookingModal"
      :eventDetails="null"
      @closeModal="closeBookingModal"
    />
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import EventTicketModal from "./EventTicketModal.vue";

export default {
  components: {
    EventTicketModal,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      mobileMenuHidden: true,
      storeInitialized: false,
      showBookingModal: false,
      navScrolled: false,
      lastScrollY: 0,
      isHeaderHidden: false,
      scrollTicking: false,
    };
  },
  beforeCreate() {
    try {
      // Basic store initialization check
      if (this.$store && typeof this.$store === "object") {
        // Check if critical methods exist
        if (
          typeof this.$store.commit === "function" &&
          typeof this.$store.dispatch === "function" &&
          this.$store.getters
        ) {
          this.storeInitialized = true;
        } else {
          console.warn(
            "HeaderSection: Store exists but critical methods are not available"
          );
        }
      } else {
        console.warn("HeaderSection: Store is not properly initialized");
      }
    } catch (error) {
      console.error("HeaderSection: Error in beforeCreate hook:", error);
    }
  },
  methods: {
    handleScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;

      requestAnimationFrame(() => {
        const currentY =
          window.scrollY ||
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          0;

        const scrolled = currentY > 10;
        if (this.navScrolled !== scrolled) {
          this.navScrolled = scrolled;
        }

        const threshold = 80;
        const delta = currentY - this.lastScrollY;

        if (currentY <= threshold) {
          if (this.isHeaderHidden) this.isHeaderHidden = false;
        } else if (delta > 6 && !this.isHeaderHidden) {
          this.isHeaderHidden = true;
        } else if (delta < -6 && this.isHeaderHidden) {
          this.isHeaderHidden = false;
        }

        this.lastScrollY = currentY;
        this.scrollTicking = false;
      });
    },
    toggleMobileMenu() {
      this.mobileMenuHidden = !this.mobileMenuHidden;

      // Toggle hamburger menu animation
      const toggleButton = document.getElementById("navbar-toggle");
      if (toggleButton) {
        if (!this.mobileMenuHidden) {
          toggleButton.classList.add("open");
        } else {
          toggleButton.classList.remove("open");
        }
      }

      if (!this.mobileMenuHidden) {
        // Prevent scrolling when menu is open
        document.body.style.overflow = "hidden";
      } else {
        // Allow scrolling when menu is closed
        document.body.style.overflow = "auto";
      }
    },
    closeMobileMenu() {
      this.mobileMenuHidden = true;
      document.body.style.overflow = "auto";

      // Remove hamburger menu animation
      const toggleButton = document.getElementById("navbar-toggle");
      if (toggleButton) {
        toggleButton.classList.remove("open");
      }
    },
    handleResize() {
      if (window.matchMedia("(min-width: 1024px)").matches) {
        this.closeMobileMenu();
      }
    },
    openBookingModal() {
      this.showBookingModal = true;
      this.closeMobileMenu(); // Close mobile menu if open
    },
    closeBookingModal() {
      this.showBookingModal = false;
    },
  },
  mounted() {
    let themeToggleDarkIcon = document.getElementById("theme-toggle-dark-icon");
    let themeToggleLightIcon = document.getElementById(
      "theme-toggle-light-icon"
    );
    let mobileThemeToggleDarkIcon = document.getElementById(
      "mobile-theme-toggle-dark-icon"
    );
    let mobileThemeToggleLightIcon = document.getElementById(
      "mobile-theme-toggle-light-icon"
    );

    // Change the icons inside the button based on previous settings
    if (
      localStorage.getItem("color-theme") === "dark" ||
      (!("color-theme" in localStorage) &&
        window.matchMedia("(prefers-color-scheme: dark)").matches)
    ) {
      themeToggleLightIcon.classList.remove("hidden");
      mobileThemeToggleLightIcon.classList.remove("hidden");
    } else {
      themeToggleDarkIcon.classList.remove("hidden");
      mobileThemeToggleDarkIcon.classList.remove("hidden");
    }

    let themeToggleBtn = document.getElementById("theme-toggle");
    let mobileThemeToggleBtn = document.getElementById("mobile-theme-toggle");

    const toggleTheme = function () {
      // toggle icons inside button
      themeToggleDarkIcon.classList.toggle("hidden");
      themeToggleLightIcon.classList.toggle("hidden");
      mobileThemeToggleDarkIcon.classList.toggle("hidden");
      mobileThemeToggleLightIcon.classList.toggle("hidden");

      // if set via local storage previously
      if (localStorage.getItem("color-theme")) {
        if (localStorage.getItem("color-theme") === "light") {
          document.documentElement.classList.add("dark");
          document.documentElement.classList.add("transition-all");
          document.documentElement.classList.add("duration-500");
          localStorage.setItem("color-theme", "dark");
        } else {
          document.documentElement.classList.remove("dark");
          document.documentElement.classList.add("transition-all");
          document.documentElement.classList.add("duration-500");
          localStorage.setItem("color-theme", "light");
        }

        // if NOT set via local storage previously
      } else {
        if (document.documentElement.classList.contains("dark")) {
          document.documentElement.classList.remove("dark");
          document.documentElement.classList.add("transition-all");
          document.documentElement.classList.add("duration-500");
          localStorage.setItem("color-theme", "light");
        } else {
          document.documentElement.classList.add("dark");
          document.documentElement.classList.add("transition-all");
          document.documentElement.classList.add("duration-500");
          localStorage.setItem("color-theme", "dark");
        }
      }

      // Force a repaint to ensure mobile menu updates immediately
      setTimeout(() => {
        const mobileMenu = document.querySelector(".mobile-menu-panel");
        if (mobileMenu) {
          // Trigger a repaint by temporarily modifying and restoring a property
          const originalOpacity = mobileMenu.style.opacity;
          mobileMenu.style.opacity = "0.99";
          mobileMenu.offsetHeight; // Force reflow
          mobileMenu.style.opacity = originalOpacity || "";
        }
      }, 50);
    };

    themeToggleBtn.addEventListener("click", toggleTheme);
    mobileThemeToggleBtn.addEventListener("click", toggleTheme);

    // Scroll listener for nav state (similar to LandingPage)
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleResize, { passive: true });
    this.handleScroll();
  },
  beforeUnmount() {
    // Make sure to reset body overflow
    document.body.style.overflow = "auto";
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
/* CSS Variables for consistency with ViewListingSection */
:root {
  --color-charcoal: #36454f;
  --color-card-dark: rgba(26, 26, 26, 0.98);
}

.header-bar {
  transform: translate3d(0, 0, 0);
  transition: transform 0.18s cubic-bezier(0.25, 0.1, 0.25, 1);
  backface-visibility: hidden;
}

.header-bar--hidden {
  transform: translate3d(0, calc(-1 * var(--app-header-height)), 0);
  pointer-events: none;
}

.header-nav {
  height: var(--app-header-height);
  min-height: var(--app-header-height);
  max-height: var(--app-header-height);
  transition: box-shadow 0.2s ease;
}

.header-container {
  height: 100%;
  max-height: var(--app-header-height);
  padding: 0 1rem;
  align-items: center;
}

.logo-link {
  display: flex;
  align-items: center;
}

.logo-title {
  font-family: "Playfair Display", serif;
  background: linear-gradient(
    135deg,
    #d4af37 0%,
    #c7ae6a 25%,
    #d4af37 50%,
    #f4d03f 75%,
    #d4af37 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 0 12px rgba(212, 175, 55, 0.25));
}

.logo-title--hero {
  font-size: clamp(1.35rem, 4.5vw, 2.25rem);
  letter-spacing: 0.02em;
}

.tagline--hero {
  margin-top: 0.125rem;
  font-size: clamp(0.7rem, 2.2vw, 0.875rem);
  line-height: 1.1;
}

@media (max-width: 1023px) {
  .menu-toggle {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    padding: 0.375rem;
    border-radius: 0.5rem;
    color: #d4af37;
    border: 1px solid rgba(212, 175, 55, 0.35);
    background: rgba(255, 255, 255, 0.04);
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }

  .menu-toggle:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(212, 175, 55, 0.55);
  }
}

.logo-frame {
  position: relative;
  padding: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  overflow: hidden;
  max-height: 100%;
}

.logo-frame::before {
  display: none;
}

.dark .logo-frame {
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.dark .logo-frame::before {
  display: none;
}

/* Header Nav - Always Black Background */
.header-nav {
  background: linear-gradient(
    180deg,
    #000000 0%,
    #0a0a0a 50%,
    #000000 100%
  ) !important;
  border-bottom: 1px solid rgba(212, 175, 55, 0.3);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.8), 
              0 0 0 1px rgba(212, 175, 55, 0.1),
              inset 0 1px 0 rgba(212, 175, 55, 0.05);
}

/* Ensure black background in both light and dark modes */
html:not(.dark) .header-nav,
html.dark .header-nav,
.header-nav {
  background: linear-gradient(
    180deg,
    #000000 0%,
    #0a0a0a 50%,
    #000000 100%
  ) !important;
}

.nav-top {
  box-shadow: none;
}

.nav-scrolled {
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.4);
}

.tagline {
  font-family: "Caveat", cursive;
  color: rgba(212, 175, 55, 0.95);
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.35);
}

/* Ensure tagline is visible on black background */
html:not(.dark) .tagline,
html.dark .tagline {
  color: rgba(212, 175, 55, 0.95);
}

/* Book Button Styling */
.book-button {
  box-shadow: 0 4px 12px rgba(185, 154, 69, 0.3);
  border: 1px solid rgba(185, 154, 69, 0.2);
}

.book-button:hover {
  box-shadow: 0 6px 20px rgba(185, 154, 69, 0.4);
}

.dark .book-button {
  box-shadow: 0 4px 12px rgba(199, 174, 106, 0.3);
  border: 1px solid rgba(199, 174, 106, 0.2);
}

.dark .book-button:hover {
  box-shadow: 0 6px 20px rgba(199, 174, 106, 0.4);
}

/* Hamburger Menu */
.toggler-icon {
  display: block;
  position: relative;
  width: 1.125rem;
  height: 2px;
  margin: 3px 0;
  background-color: var(--zulu-bronze);
  border-radius: 2px;
  transition: all 0.25s ease-in-out;
}

/* Mobile menu icon always gold on black background */
.toggler-icon {
  background-color: #d4af37 !important;
}

html:not(.dark) .toggler-icon,
html.dark .toggler-icon {
  background-color: #d4af37 !important;
}

#navbar-toggle .top-bar,
#navbar-toggle .middle-bar,
#navbar-toggle .bottom-bar {
  transform-origin: center;
}

#navbar-toggle:not(.open) .top-bar,
#navbar-toggle:not(.open) .bottom-bar {
  transform: translateY(0) rotate(0deg);
}

#navbar-toggle:not(.open) .middle-bar {
  opacity: 1;
  transform: scaleX(1);
}

#navbar-toggle.open .top-bar {
  transform: translateY(5px) rotate(45deg);
}

#navbar-toggle.open .middle-bar {
  opacity: 0;
  transform: scaleX(0.4);
}

#navbar-toggle.open .bottom-bar {
  transform: translateY(-5px) rotate(-45deg);
}

.mobile-menu-panel {
  box-shadow: -6px 0 24px rgba(0, 0, 0, 0.2);
}

.dark .mobile-menu-panel {
  box-shadow: -6px 0 28px rgba(0, 0, 0, 0.45);
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(185, 154, 69, 0.2);
  background: linear-gradient(
    90deg,
    rgba(185, 154, 69, 0.12) 0%,
    transparent 70%
  );
}

.dark .drawer-header {
  border-bottom-color: rgba(199, 174, 106, 0.2);
  background: linear-gradient(
    90deg,
    rgba(199, 174, 106, 0.1) 0%,
    transparent 70%
  );
}

.drawer-brand {
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
  min-width: 0;
}

.drawer-brand-title {
  font-family: "Playfair Display", serif;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0.02em;
  background: linear-gradient(135deg, #b99a45, #d4af37, #f4d03f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.drawer-brand-tagline {
  font-family: "Caveat", cursive;
  font-size: 0.8125rem;
  line-height: 1.1;
  color: rgba(185, 154, 69, 0.95);
}

.dark .drawer-brand-tagline {
  color: rgba(199, 174, 106, 0.9);
}

.drawer-close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  color: var(--zulu-charcoal);
  transition: background-color 0.2s ease;
}

.dark .drawer-close {
  color: var(--zulu-gold);
}

.drawer-close:hover {
  background: rgba(185, 154, 69, 0.12);
}

.dark .drawer-close:hover {
  background: rgba(199, 174, 106, 0.12);
}

.drawer-nav {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.75rem 0.75rem 0.5rem;
}

.drawer-link {
  display: block;
  padding: 0.625rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--zulu-charcoal);
  background: rgba(185, 154, 69, 0.08);
  transition: background-color 0.15s ease, color 0.15s ease;
}

.dark .drawer-link {
  color: var(--zulu-gold);
  background: rgba(199, 174, 106, 0.08);
}

.drawer-link:hover {
  background: rgba(185, 154, 69, 0.16);
}

.dark .drawer-link:hover {
  background: rgba(199, 174, 106, 0.14);
}

.drawer-cta {
  margin-top: 0.375rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #fff;
  background: linear-gradient(
    to right,
    var(--zulu-bronze),
    var(--zulu-gold)
  );
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.drawer-cta:hover {
  opacity: 0.95;
  transform: translateY(-1px);
}

.drawer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-top: 1px solid rgba(185, 154, 69, 0.12);
}

.dark .drawer-footer {
  border-top-color: rgba(199, 174, 106, 0.12);
}

.drawer-footer-label {
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(54, 69, 79, 0.65);
}

.dark .drawer-footer-label {
  color: rgba(199, 174, 106, 0.65);
}

.drawer-theme-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  border: 1px solid rgba(185, 154, 69, 0.35);
  color: var(--zulu-charcoal);
  background: rgba(255, 255, 255, 0.9);
  transition: background-color 0.15s ease;
}

.dark .drawer-theme-btn {
  color: var(--zulu-gold);
  background: #111827;
  border-color: rgba(199, 174, 106, 0.35);
}

/* Nav link styling */
.nav-link {
  position: relative;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.dark .nav-link {
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.nav-link::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 0.5rem;
  background: linear-gradient(
    135deg,
    rgba(212, 175, 55, 0.1),
    rgba(212, 175, 55, 0.05)
  );
  opacity: 0;
  transition: all 0.3s ease;
  z-index: -1;
}

/* Ensure nav link hover background is always gold-tinted */
html:not(.dark) .nav-link::before,
html.dark .nav-link::before {
  background: linear-gradient(
    135deg,
    rgba(212, 175, 55, 0.1),
    rgba(212, 175, 55, 0.05)
  );
}

.nav-link:hover::before {
  opacity: 1;
  transform: scale(1.05);
}

.nav-link::after {
  content: "";
  position: absolute;
  width: 0;
  height: 2px;
  bottom: 6px;
  left: 50%;
  background: linear-gradient(90deg, #d4af37, rgba(212, 175, 55, 0.7));
  border-radius: 1px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(-50%);
}

/* Ensure nav link underline is always gold */
html:not(.dark) .nav-link::after,
html.dark .nav-link::after {
  background: linear-gradient(90deg, #d4af37, rgba(212, 175, 55, 0.7));
}

.nav-link:hover::after {
  width: 60%;
}

.nav-link.router-link-active::after {
  width: 70%;
  background: linear-gradient(
    90deg,
    #d4af37,
    rgba(212, 175, 55, 0.8),
    #d4af37
  );
}

/* Ensure active nav link is always gold */
html:not(.dark) .nav-link.router-link-active::after,
html.dark .nav-link.router-link-active::after {
  background: linear-gradient(
    90deg,
    #d4af37,
    rgba(212, 175, 55, 0.8),
    #d4af37
  );
}

@media (min-width: 1024px) {
  .logo-title--hero {
    font-size: 2rem;
  }

  .tagline--hero {
    font-size: 0.875rem;
  }
}

@media (min-width: 1280px) {
  .logo-title--hero {
    font-size: 2.25rem;
  }
}

</style>
